import { TransactionCurrency } from '@contracts/payment/Currency';
import type { CreditCardFields } from '@src/clients/financialSettings/fields/CreditCardFields';
import Config from '@src/config/Config';
import { useAlerting } from '@src/observability/useAlerting';
import { PaymentClient } from '@src/orders/services/PaymentClient';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SafeCharge, SafeChargeField } from '../../../../nuvei';

interface Props {
  clientId: string;
  onError: () => void;
  currency?: TransactionCurrency;
}

export enum CreditCardFieldsIds {
  CardNumber = 'card-number',
  ExpirationDate = 'card-expiry',
  CVV = 'card-cvc',
}

const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap';

const style = {
  base: {
    fontFamily: 'poppins',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '1.4375em',
    letterSpacing: '0.00938em',
  },
  invalid: {
    color: 'red',
  },
};

export const useNuveiCreditCardForm = ({
  clientId,
  onError,
  currency,
}: Props) => {
  const { t } = useTranslation();
  const logger = useAlerting();
  const [isLoading, setIsLoading] = useState(false);
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [isCvvComplete, setIsCvvComplete] = useState(false);
  const [isExpiryComplete, setIsExpiryComplete] = useState(false);
  const [safeCharge, setSafeCharge] = useState<SafeCharge | undefined>();
  const [nuveiCard, setNuveiCard] = useState<SafeChargeField | undefined>(
    undefined,
  );

  const { data: authenticationData } = useQuery({
    queryKey: ['getMerchantInformation', clientId, currency],
    queryFn: async () =>
      PaymentClient.getMerchantInformation({
        currency: currency ?? TransactionCurrency.USD,
      }),
  });

  const initializeSafeCharge = useCallback(
    async (authenticationData: {
      merchantId: string;
      merchantSiteId: string;
    }) => {
      setSafeCharge(
        window.SafeCharge({
          merchantId: authenticationData.merchantId,
          merchantSiteId: authenticationData.merchantSiteId,
          env: Config.VITE_NUVEI_ENVIRONMENT,
        }),
      );
    },
    [],
  );

  useEffect(() => {
    if (authenticationData) {
      void initializeSafeCharge(authenticationData);
    }
  }, [authenticationData, initializeSafeCharge]);

  useEffect(() => {
    if (safeCharge) {
      const safeChargeFields = safeCharge.fields({
        fonts: [{ cssUrl: FONT_URL }],
        locale: 'en',
      });
      // Card number----------------------------------------------------
      const cardNumber = safeChargeFields.create('ccNumber', { style });
      cardNumber.attach(`#${CreditCardFieldsIds.CardNumber}`);
      const cardExpiry = safeChargeFields.create('ccExpiration', { style });
      cardExpiry.attach(`#${CreditCardFieldsIds.ExpirationDate}`);
      // CVV--------------------------------------------------------------
      const cardCvv = safeChargeFields.create('ccCvc', { style });
      cardCvv.attach(`#${CreditCardFieldsIds.CVV}`);
      setNuveiCard(cardNumber);

      cardNumber.on('change', ({ complete }) => setIsCardComplete(complete));
      cardExpiry.on('change', ({ complete }) => setIsExpiryComplete(complete));
      cardCvv.on('change', ({ complete }) => setIsCvvComplete(complete));
    }

    // every time the useEffect is executed, the Nuvei fields are injected as children of the identified fields.
    // This return function is used to clear the fields when the component is getting unmounted.
    // This is to avoid having duplicated fields
    // in development mode due to React StrictMode rendering everything twice & avoid any potential issues in prod
    return () => {
      removeChildrenFromDom(CreditCardFieldsIds.CardNumber);
      removeChildrenFromDom(CreditCardFieldsIds.ExpirationDate);
      removeChildrenFromDom(CreditCardFieldsIds.CVV);
    };
  }, [safeCharge]);

  const preauthorizeWithNewCard = async ({
    creditCardFields,
    onSuccess,
    saveCardOnFile,
    orderId,
    amount,
    currency,
    onPreAuthFailedButCardSaved,
    onPreAuthFailed,
  }: {
    creditCardFields: CreditCardFields;
    onSuccess: (preauthorizedAmount: number) => void;
    onPreAuthFailed: (message: string) => void;
    onPreAuthFailedButCardSaved: ({
      errorCode,
      description,
    }: {
      errorCode: string;
      description: string;
    }) => void;
    saveCardOnFile: boolean;
    orderId: string;
    amount: number;
    currency: TransactionCurrency;
  }) => {
    if (nuveiCard && authenticationData && safeCharge) {
      setIsLoading(true);
      const createPaymentArgs = {
        safeCharge,
        amount,
        currency,
        clientId,
        data: {
          ...creditCardFields,
          cardFields: nuveiCard,
        },
        onError: () => {
          onError();
          setIsLoading(false);
        },
      };
      if (saveCardOnFile) {
        await PaymentClient.createPreAuthPaymentWithCardToBeSaved({
          ...createPaymentArgs,
          logger,
          onSavedWithError: ({
            errorCode,
            creditCardId,
            description,
            transactionId,
          }) => {
            onPreAuthFailedButCardSaved({ errorCode, description });
            PaymentClient.saveCreditCardRegistration({
              clientId,
              transactionId,
              creditCardId,
            });
            setIsLoading(false);
          },
          onSuccess: ({
            transactionId,
            creditCardId,
            creditCardNumber,
            sessionToken,
          }) => {
            PaymentClient.saveCreditCardRegistration({
              clientId,
              transactionId,
              creditCardId,
            });
            PaymentClient.saveTransaction({
              transactionId,
              orderId,
              clientId,
              creditCardNumber,
              amount,
              currency,
              sessionToken,
              transactionType: 'PREAUTHORIZATION',
            });
            onSuccess(amount!);
            setIsLoading(false);
          },
        });
      } else {
        await PaymentClient.createPreAuthPaymentWithSingleUseCard({
          ...createPaymentArgs,
          onPreAuthFailed: ({ errorCode, description, status }) => {
            onPreAuthFailed(
              t('preauthorization.preauthFailed', {
                status,
                description,
                errorCode,
              }),
            );
            setIsLoading(false);
          },
          onSuccess: ({ transactionId, creditCardNumber, sessionToken }) => {
            PaymentClient.saveTransaction({
              transactionId,
              orderId,
              clientId,
              creditCardNumber,
              amount,
              currency,
              sessionToken,
              transactionType: 'PREAUTHORIZATION',
            });
            onSuccess(amount!);
            setIsLoading(false);
          },
        });
      }
    }
  };
  const makePaymentWithNewCard = async ({
    creditCardFields,
    onSuccess,
    saveCardOnFile,
    orderId,
    amount,
    currency,
    onMakePaymentFailedButCardSaved,
    onMakePaymentFailed,
  }: {
    creditCardFields: CreditCardFields;
    onSuccess: (paymentAmount: number) => void;
    onMakePaymentFailed: (message: string) => void;
    onMakePaymentFailedButCardSaved: ({
      errorCode,
      description,
    }: {
      errorCode: string;
      description: string;
    }) => void;
    saveCardOnFile: boolean;
    orderId: string;
    amount: number;
    currency: TransactionCurrency;
  }) => {
    if (nuveiCard && authenticationData && safeCharge) {
      setIsLoading(true);
      const createPaymentArgs = {
        safeCharge,
        amount,
        currency,
        clientId,
        data: {
          ...creditCardFields,
          cardFields: nuveiCard,
        },
        onError: () => {
          onError();
          setIsLoading(false);
        },
      };
      if (saveCardOnFile) {
        await PaymentClient.createMakePaymentWithCardToBeSaved({
          ...createPaymentArgs,
          logger,
          onSavedWithError: ({
            errorCode,
            creditCardId,
            description,
            transactionId,
          }) => {
            onMakePaymentFailedButCardSaved({ errorCode, description });
            PaymentClient.saveCreditCardRegistration({
              clientId,
              transactionId,
              creditCardId,
            });
            setIsLoading(false);
          },
          onSuccess: async ({
            transactionId,
            creditCardId,
            creditCardNumber,
            sessionToken,
          }) => {
            await PaymentClient.saveCreditCardRegistration({
              clientId,
              transactionId,
              creditCardId,
            });
            await PaymentClient.saveTransaction({
              transactionId,
              orderId,
              clientId,
              creditCardNumber,
              amount,
              currency,
              sessionToken,
              transactionType: 'PAYMENT',
            });
            onSuccess(amount!);
            setIsLoading(false);
          },
        });
      } else {
        await PaymentClient.createMakePaymentWithSingleUseCard({
          ...createPaymentArgs,
          onMakePaymentFail: ({ errorCode, description, status }) => {
            onMakePaymentFailed(
              t('makePayment.paymentFailed', {
                status,
                description,
                errorCode,
              }),
            );
            setIsLoading(false);
          },
          onSuccess: async ({
            transactionId,
            creditCardNumber,
            sessionToken,
          }) => {
            await PaymentClient.saveTransaction({
              transactionId,
              orderId,
              clientId,
              creditCardNumber,
              amount,
              currency,
              sessionToken,
              transactionType: 'PAYMENT',
            });
            onSuccess(amount!);
            setIsLoading(false);
          },
        });
      }
    }
  };

  const saveCard = async ({
    creditCardFields,
    onSuccess,
    onSavedWithError,
  }: {
    creditCardFields: CreditCardFields;
    onSuccess: () => void;
    onSavedWithError: ({
      errorCode,
      description,
    }: {
      errorCode: string;
      description: string;
    }) => void;
  }) => {
    if (nuveiCard && authenticationData && safeCharge) {
      setIsLoading(true);
      await PaymentClient.createPreAuthPaymentWithCardToBeSaved({
        safeCharge,
        logger,
        clientId,
        amount: 0,
        currency: TransactionCurrency[creditCardFields.currency],
        data: {
          ...creditCardFields,
          cardFields: nuveiCard,
        },
        onError: () => {
          onError();
          setIsLoading(false);
        },
        onSavedWithError: ({
          errorCode,
          description,
          creditCardId,
          transactionId,
        }) => {
          onSavedWithError({ errorCode, description });
          PaymentClient.saveCreditCardRegistration({
            clientId,
            transactionId,
            creditCardId,
          });
          setIsLoading(false);
        },
        onSuccess: ({ transactionId, creditCardId }) => {
          PaymentClient.saveCreditCardRegistration({
            clientId,
            transactionId,
            creditCardId,
          });
          onSuccess();
          setIsLoading(false);
        },
      });
    }
  };

  return {
    preauthorizeWithNewCard,
    makePaymentWithNewCard,
    saveCard,
    isLoading,
    isComplete: isCardComplete && isCvvComplete && isExpiryComplete,
  };
};

const removeChildrenFromDom = (elementId: string) => {
  const element = document.getElementById(elementId);
  if (!element) {
    return;
  }

  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
};
